/**
 * PATH: packages/email-hub/src/feature/components/rich-text/SnippetManagerModal.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-SNIPPETMODAL
 * PURPOSE: Save / insert / delete reusable compose snippets ("templates").
 *          Presentational — all persistence is owned by RichTextEditor via the
 *          snippetStore, so this stays trivially renderable and the store stays
 *          unit-tested.
 * FLOW: (save) title input + captured-block preview → onSave(title)
 *       (manage) list saved snippets → onInsert / onDelete
 * DEPENDENCIES: react, react-dom portal, lucide-react icons, snippetStore types.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FileText, Save, Trash2, X } from 'lucide-react'

import { snippetHint } from './snippetSlash'
import { SNIPPET_TITLE_MAX, type ComposeSnippet } from './snippetStore'

export function SnippetManagerModal({
  open,
  pendingHtml,
  snippets,
  onClose,
  onSave,
  onDelete,
  onInsert,
}: {
  open: boolean
  /** HTML block captured for saving (selection or whole body); '' = manage-only. */
  pendingHtml: string
  snippets: ComposeSnippet[]
  onClose: () => void
  onSave: (title: string) => void
  onDelete: (id: string) => void
  onInsert: (snippet: ComposeSnippet) => void
}) {
  const [title, setTitle] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const canSave = pendingHtml.trim().length > 0

  useEffect(() => {
    if (open) {
      setTitle('')
      if (canSave) requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open, canSave])

  if (!open) return null

  const submit = () => {
    const t = title.trim()
    if (!t || !canSave) return
    onSave(t)
    setTitle('')
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center bg-black/40 pt-[12vh]"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Email snippets"
    >
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-popover shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1 text-sm font-medium text-foreground">Snippets</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {canSave && (
          <div className="border-b border-border px-3 py-3">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Save this block as
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={title}
                maxLength={SNIPPET_TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submit()
                  }
                }}
                placeholder="e.g. Friendly intro, Payment chase…"
                aria-label="Snippet title"
                className="flex-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                type="button"
                onClick={submit}
                disabled={!title.trim()}
                className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
            </div>
            <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
              {snippetHint(pendingHtml, 120)}
            </p>
          </div>
        )}

        <div className="max-h-80 overflow-y-auto py-1">
          {snippets.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No saved snippets yet. Type “/” in the email and choose
              “Save as snippet” to reuse this block later.
            </div>
          ) : (
            snippets.map((s) => (
              <div
                key={s.id}
                className="group flex items-center gap-2 px-3 py-2 hover:bg-accent"
              >
                <button
                  type="button"
                  onClick={() => onInsert(s)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                  aria-label={`Insert snippet ${s.title}`}
                >
                  <FileText className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground">{s.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {snippetHint(s.html)}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(s.id)}
                  className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                  aria-label={`Delete snippet ${s.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
